/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	faArrowDownLeftAndArrowUpRightToCenter,
	faArrowUpRightAndArrowDownLeftFromCenter,
	faChevronDown,
	faLightbulb,
} from "@fortawesome/pro-regular-svg-icons";

import { Dispatch, SetStateAction, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import classNames from "classnames";

import { Icon } from "@/components";

import { Button } from "@/components/Button/Button";

import { PermissionRoles } from "@/enum";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { getAllUserPermissions } from "@/utils";

import { useSocket } from "../context/SocketContext";
import { ScenarioMessageTypes } from "../enums/ScenarioMessageTypes.enum";
import {
	assignableUsers,
	buildProtocolRows,
	filterProtocolRows,
	isOpen,
	ProtocolPhase,
	protocolPhases,
	StatusFilter,
} from "../utils/protocolForm";
import Chat from "./ChatComponents/Chat";
import ProtocolForm, { usePhaseLabel } from "./ProtocolForm/ProtocolForm";
import ProtocolNewEntryRow from "./ProtocolForm/ProtocolNewEntryRow";
import { useProtocolText } from "./ProtocolForm/useProtocolText";

interface IProtocolProps {
	protocolCondition: string;
	setProtocolCondition: Dispatch<SetStateAction<"MINIMIZED" | "EXPANDED" | "NORMAL">>;
	currentPhaseId: number;
	isLastPhase: boolean;
	chatPermissions?: PermissionRoles[];
	handleOpenSpoilerModal?: () => void;
	/** The scenario's phases, for the protocol's phase names. */
	phases: ProtocolPhase[];
	exerciseName: string;
}

export default function Protocol({
	protocolCondition,
	setProtocolCondition,
	currentPhaseId,
	isLastPhase,
	chatPermissions,
	handleOpenSpoilerModal,
	phases,
	exerciseName,
}: IProtocolProps) {
	const {
		protocolMessages,
		sendProtocolMessage,
		updateProtocolMessage,
		stageContentHistory,
		isCurrentUserERR,
		runUsers,
	} = useSocket();
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.${key}`);
	const tf = useProtocolText();
	const phaseLabel = usePhaseLabel(phases);
	const { user } = useAppSelector(authSelector);

	const [currentChatType, setCurrentChatType] = useState<"tasks" | "protocol" | "chat">("tasks");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
	const [phaseFilter, setPhaseFilter] = useState<number | undefined>();

	// The run's leader and every protocol writer keep the protocol; the leader is decided per
	// run, not by organisation role (BCM360-346). Everyone else reads it.
	const canKeep =
		isCurrentUserERR || getAllUserPermissions(user?.role).includes(PermissionRoles.PROTOCOL_WRITER);

	const rows = useMemo(
		() => buildProtocolRows(protocolMessages, stageContentHistory),
		[protocolMessages, stageContentHistory]
	);
	const visibleRows = useMemo(
		() => filterProtocolRows(rows, statusFilter, phaseFilter),
		[rows, statusFilter, phaseFilter]
	);
	const assignable = useMemo(() => assignableUsers(runUsers), [runUsers]);
	const openCount = rows.filter((row) => row.kind === "entry" && isOpen(row.message)).length;
	const showForm = protocolCondition !== "MINIMIZED" && currentChatType === "protocol";

	const chip = (active: boolean) =>
		classNames(
			"rounded-full border px-3 py-1.5 text-xs font-semibold",
			active
				? "border-ink bg-ink text-white"
				: "border-border bg-white text-subtle hover:border-ink"
		);

	return (
		<div
			className={`flex flex-col border border-border-soft rounded-[20px] bg-white z-30 ${
				protocolCondition === "EXPANDED"
					? "min-h-[67%] max-h-[67%]"
					: protocolCondition === "MINIMIZED"
						? " "
						: "min-h-[350px] h-full max-h-[37%]"
			}`}
		>
			<div
				className={`flex justify-between items-center p-2 ${
					protocolCondition === "MINIMIZED" ? " " : "border-b-[1px]"
				} h-[60px] pl-4 border-border`}
			>
				<div className="flex items-center gap-2">
					<div
						className={classNames(
							"text-sm font-medium cursor-pointer",
							currentChatType === "tasks"
								? "text-primary-blue"
								: "text-gray-500 hover:text-primary-blue-hover"
						)}
						onClick={() => setCurrentChatType("tasks")}
					>
						{ts("progress.tasks")}
					</div>
					<div
						className={classNames(
							"text-sm font-medium cursor-pointer",
							currentChatType === "protocol"
								? "text-primary-blue hover:primary-blue-hover"
								: "text-gray-500 hover:text-primary-blue-hover"
						)}
						onClick={() => setCurrentChatType("protocol")}
					>
						{ts("progress.protocol")}
					</div>
				</div>
				<div className="space-x-2">
					{showForm && (
						<div
							className="mr-2 inline-flex items-center gap-1.5 align-middle"
							data-testid="protocol-filters"
						>
							{(["all", "open", "done"] as StatusFilter[]).map((status) => (
								<button
									key={status}
									aria-pressed={statusFilter === status}
									className={chip(statusFilter === status)}
									type="button"
									onClick={() => setStatusFilter(status)}
								>
									{tf(`filters.${status}`, { count: openCount })}
								</button>
							))}
							{/* The chevron is the mockup's own: the native one sat on the pill's border. */}
							<span className="relative inline-flex">
								<select
									aria-label={tf("filters.phase")}
									className={classNames(
										chip(phaseFilter !== undefined),
										"h-8 cursor-pointer appearance-none pr-8"
									)}
									value={phaseFilter ?? ""}
									onChange={(event) =>
										setPhaseFilter(
											event.target.value === "" ? undefined : Number(event.target.value)
										)
									}
								>
									<option value="">{tf("filters.allPhases")}</option>
									{protocolPhases(rows).map((id) => (
										<option key={id} value={id}>
											{phaseLabel(id)}
										</option>
									))}
								</select>
								<Icon
									className={classNames(
										"pointer-events-none absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2",
										phaseFilter !== undefined ? "text-white" : "text-subtle"
									)}
									icon={faChevronDown}
								/>
							</span>
						</div>
					)}
					{handleOpenSpoilerModal && (
						<Button
							className="bg-white !w-[44px] !h-[44px] !p-0 !border !border-border-soft"
							image={<Icon className="text-primary-blue w-5 h-5" icon={faLightbulb} />}
							title=""
							onClick={() => handleOpenSpoilerModal()}
						/>
					)}
					{protocolCondition !== "EXPANDED" && protocolCondition !== "MINIMIZED" && (
						<Button
							className="bg-white !w-[44px] !h-[44px] !p-0 !border !border-border-soft"
							image={
								<Icon
									className="text-primary-blue w-5 h-5"
									icon={faArrowDownLeftAndArrowUpRightToCenter}
								/>
							}
							title=""
							onClick={() => {
								setProtocolCondition("MINIMIZED");
							}}
						/>
					)}
					<Button
						className="bg-white !w-[44px] !h-[44px] !p-0 !border !border-border-soft"
						image={
							<Icon
								className="text-primary-blue w-5 h-5"
								icon={
									protocolCondition === "EXPANDED"
										? faArrowDownLeftAndArrowUpRightToCenter
										: faArrowUpRightAndArrowDownLeftFromCenter
								}
							/>
						}
						title=""
						onClick={() => {
							if (protocolCondition === "MINIMIZED") {
								setProtocolCondition("NORMAL");
							} else if (protocolCondition === "NORMAL") {
								setProtocolCondition("EXPANDED");
							} else if (protocolCondition === "EXPANDED") {
								setProtocolCondition("NORMAL");
							}
						}}
					/>
				</div>
			</div>
			{protocolCondition !== "MINIMIZED" && currentChatType === "tasks" && (
				<div className={`flex-1 flex flex-col w-full gap-4 h-[calc(100%-60px)]`}>
					<Chat
						chatPermissions={chatPermissions}
						currentPhaseId={currentPhaseId}
						handleAction={updateProtocolMessage}
						// The same input as the Protokoll form, for those who keep the protocol (BCM360-381).
						input={
							canKeep ? (
								<ProtocolNewEntryRow
									assignable={assignable}
									currentPhaseId={currentPhaseId}
									layout="bar"
									onAdd={(entry) => sendProtocolMessage(ScenarioMessageTypes.MESSAGE, entry)}
								/>
							) : null
						}
						isLastPhase={isLastPhase}
						messages={protocolMessages}
						sendMessage={(message) =>
							sendProtocolMessage(ScenarioMessageTypes.MESSAGE, { message })
						}
						type="tasks"
					/>
				</div>
			)}
			{showForm && (
				<div className="flex h-[calc(100%-60px)] w-full flex-1 flex-col">
					<ProtocolForm
						canKeep={canKeep}
						currentPhaseId={currentPhaseId}
						exerciseName={exerciseName}
						phases={phases}
						rows={visibleRows}
						totalRows={rows.length}
					/>
				</div>
			)}
		</div>
	);
}
